import { Component } from 'react';
import { connect } from 'react-redux';
import emptyFunction from 'react-utils/emptyFunction';
import classNames from 'classnames';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { makeGetHasValidationError } from 'ContentData/selectors/validationErrors';
import ErrorIcon from 'ContentComponents/cmv2/ErrorIcon';
import H7 from 'UIComponents/elements/headings/H7';
import UITruncateString from 'UIComponents/text/UITruncateString';
import UIIconCircle from 'UIComponents/icon/UIIconCircle';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UICaret from 'UIComponents/icon/icons/UICaret';
import UIAlert from 'UIComponents/alert/UIAlert';
import Small from 'UIComponents/elements/Small';
import EditFormLabel from 'ContentComponents/cmv2/EditFormLabel';
import SanitizedLabel from 'ContentComponents/cmv2/SanitizedLabel';
import ExpandableSection from 'ContentComponents/cmv2/components/ExpandableSection';
import { EXPANDABLE_OPENER_COLOR } from 'UIComponents/accordion/ExpandableConstants';
import { GROUP } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { CALYPSO_DARK, EERIE } from 'HubStyleTokens/colors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CARET_WIDTH = 20;
const CARET_HEIGHT = 12;
const CARET_MARGIN = 12;
const DrilldownWrapper = styled.div.withConfig({
  displayName: "FieldGroup__DrilldownWrapper",
  componentId: "u3lp0j-0"
})(["cursor:pointer;&:hover{color:", ";}"], CALYPSO_DARK);
const DrilldownCaret = styled(UICaret).attrs({
  color: EXPANDABLE_OPENER_COLOR,
  height: CARET_HEIGHT,
  width: CARET_WIDTH
}).withConfig({
  displayName: "FieldGroup__DrilldownCaret",
  componentId: "u3lp0j-1"
})(["margin-left:", "px;"], CARET_MARGIN);
const makeMapStateToProps = () => {
  const getHasValidationError = makeGetHasValidationError();
  return (state, props) => ({
    hasValidationError: getHasValidationError(state, props)
  });
};
const connector = connect(makeMapStateToProps);
class FieldGroup extends Component {
  constructor(props) {
    super(props);
    this.handleFieldGroupOpenChange = this.handleFieldGroupOpenChange.bind(this);
    this.state = {
      isLabelTruncated: false
    };
  }
  get wrapperProps() {
    const {
      containsNestedGroups = false
    } = this.props;
    const classNameMap = containsNestedGroups ? 'with-nested-groups' : 'without-nested-groups';
    return {
      className: classNames('cmv2-field-group', classNameMap),
      'data-field-key': this.fieldKey
    };
  }
  get fieldKey() {
    return this.props.fieldKey.join('.');
  }
  handleFieldGroupOpenChange(evt) {
    const {
      onFieldGroupOpenChange
    } = this.props;
    onFieldGroupOpenChange(evt.target.value, this.fieldKey);
  }
  renderHelpTextTooltip() {
    const {
      help_text: helpText
    } = this.props;
    if (!helpText) return null;
    // Renders a custom UIIconCircle because otherwise ErrorIcon's UIIconCircle
    // will get stretched if rendered next to the UIIcon from HelpTooltip
    return /*#__PURE__*/_jsx(UITooltip, {
      title: /*#__PURE__*/_jsx(SanitizedLabel, {
        label: helpText
      }),
      children: /*#__PURE__*/_jsx(UIIconCircle, {
        className: "p-all-0",
        size: 16,
        borderWidth: 0,
        color: EERIE,
        name: "info",
        "data-selenium": `${this.fieldKey}:helpText`
      })
    });
  }
  renderErrorTooltip(props = {}) {
    const {
      hasValidationError
    } = this.props;
    const {
      iconClassName
    } = props;
    if (!hasValidationError) return null;
    return /*#__PURE__*/_jsx(UITooltip, {
      placement: "top right",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ContentComponents.cmv2.errorIcon.groupEditMessage"
      }),
      children: /*#__PURE__*/_jsx("span", {
        children: /*#__PURE__*/_jsx(ErrorIcon, {
          className: iconClassName,
          "data-selenium": `${this.fieldKey}:validationError`
        })
      })
    });
  }

  // Returns fixed children for a group label
  renderFixedChildren() {
    const {
      GroupLabelChild,
      fieldKey
    } = this.props;
    if (!GroupLabelChild) return null;
    return /*#__PURE__*/_jsx(GroupLabelChild, {
      fieldKey: fieldKey
    });
  }
  renderLabel() {
    const {
      label,
      LabelComponent,
      fieldKey,
      allowFieldReset,
      onFieldReset
    } = this.props;
    return /*#__PURE__*/_jsx(EditFormLabel, {
      fieldKey: fieldKey,
      fieldType: GROUP,
      onFieldReset: onFieldReset,
      label: label,
      LabelComponent: LabelComponent,
      allowFieldReset: allowFieldReset
    });
  }
  renderNewFieldGroupLabelAndHelpText() {
    return /*#__PURE__*/_jsxs("div", {
      className: "cmv2-field-group-label-and-icons align-center",
      children: [/*#__PURE__*/_jsx(H7, {
        className: "cmv2-field-group-label m-y-0",
        children: /*#__PURE__*/_jsx(UITruncateString, {
          className: "cmv2-field-group-label-truncated",
          useFlex: true,
          children: this.renderLabel()
        })
      }), /*#__PURE__*/_jsxs("span", {
        className: "align-center",
        children: [this.renderFixedChildren(), this.renderHelpTextTooltip()]
      })]
    });
  }
  renderWithNestedGroups() {
    const {
      onEditGroupClick = emptyFunction
    } = this.props;
    return /*#__PURE__*/_jsx(DrilldownWrapper, Object.assign({}, this.wrapperProps, {
      "data-test-id": "cmv2-field-group-drilldown",
      onClick: onEditGroupClick,
      children: /*#__PURE__*/_jsx(UIMediaObject, {
        align: "center",
        "data-test-id": "cmv2-field-group-drilldown-content",
        itemLeft: this.renderErrorTooltip({
          iconClassName: 'm-bottom-1'
        }),
        itemRight: /*#__PURE__*/_jsx(DrilldownCaret, {
          className: "m-top-2",
          "data-test-id": "cmv2-field-group-drilldown-caret"
        }),
        children: this.renderNewFieldGroupLabelAndHelpText()
      })
    }));
  }
  renderWithoutNestedGroups() {
    const {
      children,
      disabled,
      disabledMessage,
      fieldGroupOpen = false,
      inline_help_text: inlineHelpText,
      useOnDark
    } = this.props;
    return /*#__PURE__*/_jsxs(ExpandableSection, Object.assign({}, this.wrapperProps, {
      onOpenChange: this.handleFieldGroupOpenChange,
      open: fieldGroupOpen,
      useOnDark: useOnDark,
      titleText: /*#__PURE__*/_jsx(UIMediaObject, {
        align: "center",
        itemLeft: this.renderErrorTooltip({
          iconClassName: 'm-bottom-1'
        }),
        children: this.renderNewFieldGroupLabelAndHelpText()
      }),
      children: [inlineHelpText && /*#__PURE__*/_jsx(Small, {
        className: "cmv2-inline-help-text m-top-4",
        use: "help",
        children: /*#__PURE__*/_jsx(SanitizedLabel, {
          label: inlineHelpText,
          useStrict: true
        })
      }), disabled && disabledMessage && /*#__PURE__*/_jsx(UIAlert, {
        className: "m-x-6 m-y-3",
        type: "tip",
        children: /*#__PURE__*/_jsx(SanitizedLabel, {
          label: disabledMessage,
          useStrict: true
        })
      }), children]
    }));
  }
  render() {
    const {
      containsNestedGroups = false
    } = this.props;
    return containsNestedGroups ? this.renderWithNestedGroups() : this.renderWithoutNestedGroups();
  }
}
export default connector(FieldGroup);